import * as THREE from 'three';
import { setGlobalLineSegments } from '../../utils/getActiveFloorPlanId';

// Spatial grid for segment-segment intersection lookup. O(N²) all-pairs check
// scales poorly; bucketing segments by which grid cells they overlap lets us
// only compare pairs that share at least one cell → near O(N*k) on uniform data.
class SegmentSpatialGrid {
  constructor(segments, targetCellSize = 50) {
    this.cells = new Map(); // "cx,cy" → array of segment indices
    if (!segments || segments.length === 0) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const s of segments) {
      if (s.start.x < minX) minX = s.start.x;
      if (s.end.x   < minX) minX = s.end.x;
      if (s.start.y < minY) minY = s.start.y;
      if (s.end.y   < minY) minY = s.end.y;
      if (s.start.x > maxX) maxX = s.start.x;
      if (s.end.x   > maxX) maxX = s.end.x;
      if (s.start.y > maxY) maxY = s.start.y;
      if (s.end.y   > maxY) maxY = s.end.y;
    }
    if (!isFinite(minX)) return;
    const span = Math.max(maxX - minX, maxY - minY) || 1;
    // Hücre büyüklüğü span'in çok altına düşerse hücre sayısı patlar; üst sınır.
    let cellSize = targetCellSize;
    const maxCellsPerAxis = 200;
    if (span / cellSize > maxCellsPerAxis) cellSize = span / maxCellsPerAxis;
    this.cellSize = cellSize;
    this.minX = minX; this.minY = minY;
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      const c1x = Math.floor((s.start.x - minX) / cellSize);
      const c1y = Math.floor((s.start.y - minY) / cellSize);
      const c2x = Math.floor((s.end.x   - minX) / cellSize);
      const c2y = Math.floor((s.end.y   - minY) / cellSize);
      const xMin = Math.min(c1x, c2x), xMax = Math.max(c1x, c2x);
      const yMin = Math.min(c1y, c2y), yMax = Math.max(c1y, c2y);
      for (let cy = yMin; cy <= yMax; cy++) {
        for (let cx = xMin; cx <= xMax; cx++) {
          const key = cx + ',' + cy;
          let bucket = this.cells.get(key);
          if (!bucket) { bucket = []; this.cells.set(key, bucket); }
          bucket.push(i);
        }
      }
    }
  }
  // Verilen segmentle aynı hücreyi paylaşan segment index'lerini döner (idx > i, duplicate önler).
  forEachNearbyAfter(segments, i, callback) {
    if (!this.cellSize) return;
    const s = segments[i];
    const c1x = Math.floor((s.start.x - this.minX) / this.cellSize);
    const c1y = Math.floor((s.start.y - this.minY) / this.cellSize);
    const c2x = Math.floor((s.end.x   - this.minX) / this.cellSize);
    const c2y = Math.floor((s.end.y   - this.minY) / this.cellSize);
    const xMin = Math.min(c1x, c2x), xMax = Math.max(c1x, c2x);
    const yMin = Math.min(c1y, c2y), yMax = Math.max(c1y, c2y);
    const seen = new Set();
    for (let cy = yMin; cy <= yMax; cy++) {
      for (let cx = xMin; cx <= xMax; cx++) {
        const bucket = this.cells.get(cx + ',' + cy);
        if (!bucket) continue;
        for (let k = 0; k < bucket.length; k++) {
          const j = bucket[k];
          if (j <= i) continue;
          if (seen.has(j)) continue;
          seen.add(j);
          callback(j);
        }
      }
    }
  }
}

// 2D line-line intersection (segments only, returns null if parallel or outside)
function segmentIntersection(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-10) return null; // parallel
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / denom;
  if (t < 0.001 || t > 0.999 || u < 0.001 || u > 0.999) return null; // outside segments (with small margin to avoid endpoint duplicates)
  return new THREE.Vector3(p1.x + t * d1x, p1.y + t * d1y, 0);
}

export function getSnapPointsFromScene(
  scene,
  floorPlans = [],
  wires = [],
  snapGroup = null,
  options = {}
) {
  const { allowedTypes = null } = options;
  const points = [];
  const segments = []; // Line segments for dynamic snap computation

  if (snapGroup) {
    snapGroup.clear();
    snapGroup.visible = false;
  }

  // ✅ floorPlanId ve source parametreleri eklendi
  const addSnapPoint = (pos, type, no = null, parent = null, floorPlanId = null, source = null) => {
    if (allowedTypes && !allowedTypes.includes(type)) return;
    points.push({ no, position: pos.clone(), type, parent, floorPlanId, source });
    if (snapGroup) {
      const marker = new THREE.Object3D();
      marker.position.copy(pos);
      marker.userData = { type, no, floorPlanId, source };
      snapGroup.add(marker);
    }
  };

  // ✅ Çizgi tabanlı objelerden snap noktası çıkaran yardımcı fonksiyon
  const extractLineSnapPoints = (obj, floorPlanId, source) => {
    const isLineType = obj.type === 'Line' || obj.type === 'LineLoop' || obj.type === 'LineSegments';
    if (!isLineType || !obj.userData?.isSelectable) return;

    const posAttr = obj.geometry?.attributes?.position;
    if (!posAttr) return;

    const vertexCount = posAttr.count;
    const vertices = [];

    for (let i = 0; i < vertexCount; i++) {
      const localVertex = new THREE.Vector3().fromBufferAttribute(posAttr, i);
      const worldVertex = localVertex.clone();
      obj.localToWorld(worldVertex);
      vertices.push(worldVertex);
    }

    const isFullCircle = obj.type === 'LineLoop' && vertexCount >= 32;
    const isArc = obj.type === 'Line' && vertexCount > 3;
    const isEllipse = obj.userData?.type === 'ellipse';

    if (isFullCircle || isArc || isEllipse) {
      let center, radius;

      if (obj.userData.center && obj.userData.radius) {
        center = new THREE.Vector3(
          obj.userData.center.x,
          obj.userData.center.y,
          obj.userData.center.z || 0
        );
        obj.localToWorld(center);
        radius = obj.userData.radius;
      } else {
        center = new THREE.Vector3();
        vertices.forEach(v => center.add(v));
        center.divideScalar(vertexCount);
        radius = vertices[0].distanceTo(center);
      }

      addSnapPoint(center, 'center', obj.uuid, obj.userData, floorPlanId, source);

      if (obj.userData.type !== 'panelCon_out') {
        const angles = isEllipse
          ? [0, Math.PI * 0.5, Math.PI, Math.PI * 1.5]
          : [0, Math.PI * 0.25, Math.PI * 0.5, Math.PI * 0.75,
             Math.PI, Math.PI * 1.25, Math.PI * 1.5, Math.PI * 1.75];

        angles.forEach((angle, idx) => {
          const x = center.x + radius * Math.cos(angle);
          const y = center.y + radius * Math.sin(angle);
          const pos = new THREE.Vector3(x, y, 0);
          addSnapPoint(pos, idx % 2 === 0 ? 'quadrant' : 'diagonal', obj.uuid, obj.userData, floorPlanId, source);
        });
      }

      if (isArc) {
        addSnapPoint(vertices[0], 'start', obj.uuid, obj.userData, floorPlanId, source);
        addSnapPoint(vertices[vertices.length - 1], 'end', obj.uuid, obj.userData, floorPlanId, source);
      }
    }

    // Vertex snap points (start, end, vertex)
    vertices.forEach((vertex, idx) => {
      const type = idx === 0 ? 'start' : idx === vertexCount - 1 ? 'end' : 'vertex';
      addSnapPoint(vertex, type, obj.uuid, obj.userData, floorPlanId, source);
    });

    // ✅ FIX: Per-segment midpoints for ALL lines (not just polyline centroid)
    if (vertices.length >= 2 && !isFullCircle) {
      for (let i = 0; i < vertices.length - 1; i++) {
        const mid = new THREE.Vector3().addVectors(vertices[i], vertices[i + 1]).multiplyScalar(0.5);
        addSnapPoint(mid, 'midpoint', obj.uuid, obj.userData, floorPlanId, source);
      }
    }

    // ✅ Collect line segments for dynamic snap (nearest/perpendicular)
    if (!isFullCircle && vertices.length >= 2) {
      for (let i = 0; i < vertices.length - 1; i++) {
        segments.push({
          start: vertices[i].clone(),
          end: vertices[i + 1].clone(),
          floorPlanId,
          source,
        });
      }
    }
  };

  // ✅ floorPlan objeleri için UUID set (section 3'te duplicate önlemek için)
  const processedUUIDs = new Set();

  // 1) FloorPlan içindeki çizgiler
  floorPlans.forEach(fp => {
    const currentFloorPlanId = fp.id;
    if (!fp.objects || !Array.isArray(fp.objects)) return;
    fp.objects.forEach(obj => {
      const t = obj.userData?.type;
      if (!t) return;

      processedUUIDs.add(obj.uuid);

      // ✅ Buat ve panel snap noktaları → tesisatCizim
      if (t === 'buatCircle') addSnapPoint(obj.position, obj.userData.snapType || t, obj.uuid, obj, currentFloorPlanId, 'tesisatCizim');
      if (t === 'buatLightingSquare') addSnapPoint(obj.position, obj.userData.snapType || t, obj.uuid, obj.parent, currentFloorPlanId, 'tesisatCizim');
      if (t === 'buatLightingCircle') addSnapPoint(obj.position, obj.userData.snapType || t, obj.uuid, obj.parent, currentFloorPlanId, 'tesisatCizim');
      if (t === 'panelCon_out') addSnapPoint(obj.position, obj.userData.snapType || t, obj.uuid, obj, currentFloorPlanId, 'tesisatCizim');

      // ✅ Çizgi tabanlı snap noktaları → kullaniciCizim
      extractLineSnapPoints(obj, currentFloorPlanId, 'kullaniciCizim');
    });
  });

  // ⚡ PERF: tüm Line'ları tek geçişte uuid→obj harita → per-wire getObjectByProperty
  // (O(wire×sahne)) yerine O(1) lookup (snapWallHandler'daki fix'in ikizi).
  const _lineByUuid = new Map();
  scene.traverse(o => { if (o.isLine) _lineByUuid.set(o.uuid, o); });

  // 2) Wires içindeki çizgiler (Redux wireSlice'tan gelenler) → tesisatCizim
  wires.forEach(w => {
    const obj = _lineByUuid.get(w.sceneUUID);
    if (!obj) return;

    const posAttr = obj.geometry?.attributes?.position;
    if (!posAttr) return;

    const vertices = [];
    for (let i = 0; i < posAttr.count; i++) {
      const localVertex = new THREE.Vector3().fromBufferAttribute(posAttr, i);
      const worldVertex = localVertex.clone();
      obj.localToWorld(worldVertex);
      vertices.push(worldVertex);

      const type = i === 0 ? 'start' : i === posAttr.count - 1 ? 'end' : 'vertex';
      addSnapPoint(worldVertex, type, obj.uuid, obj.userData, null, 'tesisatCizim');
    }

    // ✅ FIX: Per-segment midpoints (not centroid)
    for (let i = 0; i < vertices.length - 1; i++) {
      const mid = new THREE.Vector3().addVectors(vertices[i], vertices[i + 1]).multiplyScalar(0.5);
      addSnapPoint(mid, 'midpoint', obj.uuid, obj.userData, null, 'tesisatCizim');
      segments.push({
        start: vertices[i].clone(),
        end: vertices[i + 1].clone(),
        floorPlanId: null,
        source: 'tesisatCizim',
      });
    }
  });

  // 3) Scene'deki objeler — DXF (mimari) ve kullanıcı çizimleri
  if (scene) {
    scene.traverse(obj => {
      if (!obj.visible) return;
      if (!obj.userData?.isSelectable) return;
      if (processedUUIDs.has(obj.uuid)) return; // floorPlan'da zaten işlendi
      if (obj.userData.source === 'dxf') {
        extractLineSnapPoints(obj, null, 'mimariCizim');
      } else {
        // Kullanıcı çizimleri (source etiketi yok veya farklı)
        extractLineSnapPoints(obj, null, 'kullaniciCizim');
      }
    });
  }

  // 4) ✅ Pre-compute intersection points — spatial grid ile yalnızca aynı hücreyi
  //    paylaşan segment çiftlerini karşılaştır. O(N²) → ~O(N*k) (k = hücre yoğunluğu).
  //    Eski limit 2000'di; spatial grid sayesinde 10000'e kadar makul kalır.
  if (segments.length > 0 && segments.length <= 10000) {
    const grid = new SegmentSpatialGrid(segments);
    for (let i = 0; i < segments.length; i++) {
      grid.forEachNearbyAfter(segments, i, (j) => {
        const ip = segmentIntersection(
          segments[i].start, segments[i].end,
          segments[j].start, segments[j].end
        );
        if (ip) {
          addSnapPoint(ip, 'intersection', null, null,
            segments[i].floorPlanId || segments[j].floorPlanId,
            segments[i].source || segments[j].source
          );
        }
      });
    }
  }

  // ✅ Store segments globally for dynamic snap types (nearest, perpendicular)
  setGlobalLineSegments(segments);

  return points;
}
